import { FC, useState } from 'react';
import { useGetList, useGetMany, useListContext, useNotify, useRefresh, useTranslate } from 'react-admin';
import { LinkOff } from '@mui/icons-material';
import {
  Alert,
  Autocomplete as MuiAutocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { usePermission } from 'providers/auth/permissionProvider';
import { DetachUnit, useDetachUnitsFromChannel } from 'services/api/useDetachUnitsFromChannel';

type DialogState = 'closed' | 'confirm' | 'results';

interface DetachResult {
  succeeded: Array<{ id: number; serial: string | null; hostname: string | null }>;
  failed: Array<{ id: number; serial: string | null; hostname: string | null; error: string }>;
  successCount: number;
  failureCount: number;
}

interface ChannelOption {
  id: number;
  name: string;
}

interface DetachFromChannelBulkActionProps {
  currentChannelId: number;
}

export const DetachFromChannelBulkAction: FC<DetachFromChannelBulkActionProps> = ({ currentChannelId }) => {
  const { selectedIds, onUnselectItems } = useListContext();
  const { hasPermission } = usePermission();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { detach, loading } = useDetachUnitsFromChannel();

  const [dialogState, setDialogState] = useState<DialogState>('closed');
  const [results, setResults] = useState<DetachResult | null>(null);
  const [selectedTargetChannel, setSelectedTargetChannel] = useState<ChannelOption | null>(null);

  // Fetch ALL selected units by ID — useListContext().data only contains the
  // current page, so cross-page selections would be silently dropped otherwise.
  const { data: selectedUnitsData = [], isLoading: selectedUnitsLoading } = useGetMany<{
    id: number;
    serial?: string | null;
    hostname?: string | null;
    hardware_revision_id?: number;
  }>('units', { ids: selectedIds }, { enabled: dialogState === 'confirm' && selectedIds.length > 0 });

  const selectedUnits: DetachUnit[] = selectedUnitsData.map((unit) => ({
    id: unit.id,
    serial: unit.serial ?? null,
    hostname: unit.hostname ?? null,
  }));

  // All units in one channel share the same hardware revision.
  const hardwareRevisionId = selectedUnitsData[0]?.hardware_revision_id;

  const { data: channels = [], isLoading: channelsLoading } = useGetList<ChannelOption>(
    'update_channels',
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: 'name', order: 'ASC' },
      filter: {
        active: true,
        ...(hardwareRevisionId !== undefined ? { hardware_revision_id: hardwareRevisionId } : {}),
      },
    },
    { enabled: dialogState === 'confirm' },
  );

  // Exclude the channel the units are currently in from the target options.
  const channelOptions = channels.filter((c) => c.id !== currentChannelId);

  if (!hasPermission('manage_units') || selectedIds.length === 0) return null;

  const getUnitLabel = (unit: { id: number; serial?: string | null; hostname?: string | null }): string =>
    unit.serial ?? unit.hostname ?? String(unit.id);

  const handleOpenConfirm = () => {
    setResults(null);
    setSelectedTargetChannel(null);
    setDialogState('confirm');
  };

  const handleCancel = () => {
    setSelectedTargetChannel(null);
    setDialogState('closed');
  };

  const handleDetach = async () => {
    if (!selectedTargetChannel) return;
    const result = await detach(selectedUnits, selectedTargetChannel.id);
    setResults(result);
    setDialogState('results');

    if (result.failureCount === 0) {
      notify('resources.update_channels.units.detach_success_all', {
        type: 'success',
        messageArgs: { count: result.successCount, channel: selectedTargetChannel.name },
      });
    } else {
      notify('resources.update_channels.units.detach_success_partial', {
        type: 'warning',
        messageArgs: {
          successCount: result.successCount,
          failureCount: result.failureCount,
          channel: selectedTargetChannel.name,
        },
      });
    }
  };

  const handleCloseResults = () => {
    onUnselectItems();
    refresh();
    setDialogState('closed');
  };

  return (
    <>
      <Button
        onClick={handleOpenConfirm}
        startIcon={<LinkOff />}
        color="error"
        sx={{
          borderRadius: '30px',
          borderTopRightRadius: '10px',
          textTransform: 'none',
        }}
      >
        {translate('resources.update_channels.units.detach_button')}
      </Button>

      {/* Channel selection + confirmation dialog */}
      <Dialog open={dialogState === 'confirm'} onClose={handleCancel} maxWidth="xs" fullWidth>
        <DialogTitle>{translate('resources.update_channels.units.detach_confirm_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {translate('resources.update_channels.units.detach_confirm_body', {
              count: selectedIds.length,
            })}
          </Typography>
          <MuiAutocomplete<ChannelOption>
            options={channelOptions}
            getOptionLabel={(o) => o.name}
            value={selectedTargetChannel}
            onChange={(_, value) => setSelectedTargetChannel(value)}
            loading={channelsLoading}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('resources.update_channels.units.detach_target_channel_label')}
                size="small"
                required
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>
            {translate('resources.update_channels.units.detach_cancel_button')}
          </Button>
          <Button
            onClick={handleDetach}
            color="error"
            variant="contained"
            disabled={loading || selectedUnitsLoading || !selectedTargetChannel}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              translate('resources.update_channels.units.detach_confirm_button')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Results Dialog */}
      <Dialog open={dialogState === 'results'} onClose={handleCloseResults} maxWidth="sm" fullWidth>
        <DialogTitle>{translate('resources.update_channels.units.detach_confirm_title')}</DialogTitle>
        <DialogContent>
          <Box>
            {results && results.succeeded.length > 0 && (
              <>
                <Alert severity="success" sx={{ mb: 1 }}>
                  {translate('resources.update_channels.units.results_success_heading', {
                    count: results.succeeded.length,
                  })}
                </Alert>
                <List dense>
                  {results.succeeded.map((unit) => (
                    <ListItem key={unit.id}>
                      <ListItemText primary={getUnitLabel(unit)} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
            {results && results.failed.length > 0 && (
              <>
                <Alert severity="error" sx={{ mb: 1, mt: results.succeeded.length > 0 ? 2 : 0 }}>
                  {translate('resources.update_channels.units.results_failure_heading', {
                    count: results.failed.length,
                  })}
                </Alert>
                <List dense>
                  {results.failed.map((unit) => (
                    <ListItem key={unit.id}>
                      <ListItemText primary={getUnitLabel(unit)} secondary={unit.error} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResults} variant="contained">
            {translate('resources.update_channels.units.detach_close_button')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
